import { useCallback, useEffect, useState } from 'react';
import type {
  BloomRuntimeConfig,
  ChannelLineup,
  ChannelSummary,
  OnairChannelLineup,
  Tv2mLinkContext,
} from '@apm/contracts';
import type { AuthApi } from '../auth/use-auth.js';
import { fetchChannels, readLastItems, readSavedChannel, writeSavedChannel } from '../channels.js';
import { entryStartFor } from '../last-items.js';
import { decideAutoStart } from '../auto-start.js';
import { buffersUnlocked, playbackBuffers } from '../playback-buffers.js';
import { captureFreezeFrame } from '../freeze-frame.js';
import { resolveChannel } from '../channel-selection.js';
import { HOME_FOR_INSTALLED, entrySurface } from '../home/entry-surface.js';
import { config } from '../config.js';
import { useT } from '../i18n/LocaleProvider.js';
import { AppHeader } from '../components/AppHeader.js';
import { ChannelSheet } from '../components/ChannelSheet.js';
import { FreezeFrame } from '../components/FreezeFrame.js';
import { toEpgRows } from '../epg.js';
import { useFavoriteChannels } from '../favorite-channels.js';
import { fetchLineup, fetchOnairLineups } from '../lineup-client.js';
import { useAdsEnabled } from '../ads/ads-enabled.js';
import { EpgScreen } from './EpgScreen.js';
import { HomeScreen } from './HomeScreen.js';
import { OnairScreen } from './OnairScreen.js';
import { WatchScreen } from './WatchScreen.js';
import type { Tv2mApi } from '../useTv2m.js';

/**
 * 시청 화면 앞에 서는 「지금 볼 채널」 판정 (`docs/23` §7.2).
 *
 * **판정은 순수 함수가 하고 이 컴포넌트는 재료만 모은다** — 공개 목록·`?channel=`·저장값.
 * 그래야 규칙이 node 환경 테스트로 덮인다 (`channel-selection.test.ts`).
 *
 * **`AuthGate` 가 로그인을 컴포넌트 경계로 강제한 것과 같은 자리다.** 채널이 정해지기
 * 전에는 `WatchScreen` 이 마운트되지 않으므로 리시버가 채널 없이 설 수 없다.
 *
 * 실패해도 **화면은 뜬다** — `fetchChannels` 가 던지지 않고 빈 배열을 준다. 그때는
 * 「방송 중인 채널이 없습니다」이고, 서버가 꺼진 것과 편성이 빈 것을 시청자는 구분할
 * 필요가 없다 (원인은 진단 화면과 서버 로그가 갖는다).
 */
/** 홈이 편성표를 기다리는 상한. 넘기면 기기 저장값의 채널로 먼저 선다 */
const LINEUP_WAIT_CAP_MS = 3000;

export function ChannelGate({
  runtime,
  tv2m,
  auth,
  lastContext,
  onOpenLast,
  /** `/channels` 로 직접 들어왔다. 저장값이 있어도 목록부터 그린다 */
  forceList,
}: {
  runtime: BloomRuntimeConfig;
  tv2m: Tv2mApi;
  auth: AuthApi;
  lastContext: Tv2mLinkContext | null;
  onOpenLast: () => void;
  forceList: boolean;
}) {
  const t = useT();
  const [channels, setChannels] = useState<readonly ChannelSummary[] | null>(null);
  /** 사람이 목록에서 고른 값. 판정 결과를 이긴다 */
  const [picked, setPicked] = useState<string | null>(null);
  const [listing, setListing] = useState(forceList);
  /**
   * 전환 중. **이 값이 `null` 이 아닌 동안 `WatchScreen` 을 내린다** — 그것이
   * 절대 규칙 27 의 1번(옛 인스턴스 파괴)이다. `key` 교체로도 React 가 정리를 먼저
   * 돌리지만, **그때의 파괴는 부수 효과이고 여기서는 절차다** — 한 커밋 안에서
   * 일어나는 순서에 기대지 않고 렌더를 한 번 비워 눈으로도 보이게 한다.
   */
  const [switching, setSwitching] = useState<string | null>(null);
  /**
   * 전환 뒤에는 진입 게이트를 다시 묻지 않는다. 자동재생 정책 때문에 그 화면이 있는
   * 것인데(`docs/06` 「진입 흐름」) **전환을 부른 것 자체가 사용자 제스처**이고, 이미
   * 이 문서에서 미디어가 재생된 뒤다. 첫 진입에서는 그대로 게이트를 거친다.
   *
   * **그 문장이 WebKit 에서는 거짓이었다** (2026-08-11, iPhone 실기) — 전환은 제스처
   * 밖에서 `start()` 를 부르고 `<video>` 를 새로 만들었다. **v19 가 뒤쪽을 없앴다**
   * (엘리먼트를 다시 쓴다). 아래 ★.
   *
   * **초기값이 `buffersUnlocked()` 인 이유가 그 연장이다.** 게이트가 필요한 것은
   * **엘리먼트가 허가를 얻는 첫 한 번**뿐이고, 그 뒤에는 같은 문서·같은 엘리먼트다 —
   * 상품 화면에서 `[방송 보기]` 로 돌아올 때(`pushState` 라 같은 문서다) 게이트가 다시
   * 뜨던 것이 이 한 줄로 사라진다. 새 문서에서는 거짓으로 돌아가 게이트가 제자리에 선다.
   *
   * ★★ **v26 U10 — 설치된 앱은 새 문서에서도 묻지 않는다** (절대 규칙 72).
   * `decideAutoStart()` 가 **설치 ∧ 자동재생 정책 허용**일 때만 참이다 — 브라우저 탭과
   * 아이폰 홈 화면 앱은 여기서 거짓이라 **지금 그대로 게이트**다.
   *
   * **둘을 `||` 로 잇는 것이 옳다.** 근거가 서로 무관하다 — 앞은 「이 문서가 이미 허가를
   * 얻었다」이고 뒤는 「이 표면은 애초에 물을 필요가 없다」다. 그래서 `data-auto-start` 도
   * 뒤엣것만 적는다(`auto-start.ts` 의 그 주석).
   *
   * **판정이 틀렸을 때 되돌리는 자리는 여기가 아니라 `useReceiver` 다** — 거부는 재생을
   * 시도해야 알 수 있고, 그때 게이트를 다시 세우는 것이 자물쇠 ②다.
   */
  /**
   * v32. **설치된 앱의 자동 시작 판정** — `decideAutoStart()` 는 `data-auto-start` 를 문서에
   * 적으므로 한 번만 부른다. 아래 `autoStart` 와 `entrySurface` 둘이 이 값을 읽는다.
   */
  const [installedAutoStart] = useState(() => decideAutoStart());
  const [autoStart, setAutoStart] = useState(() => buffersUnlocked() || installedAutoStart);
  /**
   * ★ v32 — **홈** (`docs/63` · 절대 규칙 81). 시청 화면의 「홈」 버튼이 켠다. 홈의 OK 가 끈다.
   * 첫 진입의 홈은 이 값이 아니라 `entrySurface` 의 나머지 조건(허가 없음)에서 나온다.
   */
  const [home, setHome] = useState(false);
  /**
   * 전환과 함께 세울 첫 회차 (v25). `null` 이면 지금까지의 전환 그대로다.
   *
   * **`switching` 과 같은 커밋에 넣는다** — 한 이벤트 핸들러 안이라 React 가 한 배치로
   * 처리하고, 다음 렌더에서 `picked` 와 함께 새 `WatchScreen` 에 실린다. 나눠 넣으면
   * **회차 없이 선 화면이 한 번 그려지고** 그것이 곧 「플레이리스트를 두 번 받는」 안이다.
   */
  const [startAt, setStartAt] = useState<string | null>(null);
  /**
   * v29. **편성표에서 고른 전환인가** (2026-09-06 무대 신고).
   *
   * 참이면 시트를 **전환을 건너 유지한다** — 이 컴포넌트가 빈 렌더에서 그리고, 새 화면이
   * 열린 채로 마운트한다. 그러지 않으면 그 사이를 **잡아 둔 옛 채널의 프레임**이 덮어
   * 「눌렀더니 원래 채널이 한 번 더 나온다」가 된다.
   *
   * **전환마다 다시 정해진다**(`switchTo` 가 매번 쓴다) — 남겨 두면 ▲▼ 전환에서도
   * 편성표가 열린 채 시작한다.
   */
  const [keepEpg, setKeepEpg] = useState(false);
  /** 편성표. **못 받아도 채널 목록은 그대로 뜬다** — D10 을 지키는 마지막 겹이다 */
  const [lineups, setLineups] = useState<readonly ChannelLineup[]>([]);
  /**
   * v32. **편성표가 한 번 온 뒤에야 홈을 세운다** (2026-09-10 실기). 홈의 대상 채널이 계정의 최근
   * 시청(`resumeUpdatedAt`)으로 정해지므로, 채널 목록만으로 먼저 그리면 기기 저장값의 채널이 한
   * 번 보였다가 편성표가 오며 바뀐다 — TV 에서는 그것이 깜빡임이다. 실패(`null`)도 「왔다」다.
   * 시청·편성표 화면은 기다리지 않는다(지금 그대로).
   */
  const [lineupReady, setLineupReady] = useState(false);
  const [favorites, toggleFavorite] = useFavoriteChannels();
  const [adsEnabled] = useAdsEnabled();

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    // **`deviceId` 를 싣는다.** 서버는 지금 로깅만 하지만 개인화가 들어오면 "이 시청자에게
    // 보일 채널 목록" 이 되는 자리다 (절대 규칙 2 와 같은 이유).
    void fetchChannels(tv2m.init?.deviceId, controller.signal).then((items) => {
      if (alive) setChannels(items);
    });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [tv2m.init]);

  /**
   * 편성표 (v25). **채널 목록과 따로 부른다** — 저쪽은 무인증·캐시 가능이고 이쪽은
   * 시청자마다 갈린다(D10 을 뒤집지 않는 방법 · `docs/51` §2.4).
   *
   * **실패해도 아무 일이 없다** — `fetchLineup` 이 던지지 않고 `null` 을 주며, 그때
   * 들고 있던 것을 지킨다. 첫 호출이면 화면은 채널 목록만으로 그려지고 카드 자리에
   * 자리표시가 뜬다.
   *
   * 토큰이 바뀌면 다시 부른다 — 로그인하면 그 순간 이어보기 지점이 카드에 나타난다.
   *
   * ── ★★ v27/TG15 — **편성표를 여는 순간에도 다시 받는다** ──────
   *
   * 안 하면 이 응답이 **부팅(또는 로그인) 시점의 스냅숏으로 굳는다.** 그리고 행의 출처가
   * 둘이라 그 낡음이 **채널을 오갈 때마다 눈에 보인다**:
   *
   * | 행 | 출처 | 나이 |
   * |---|---|---|
   * | 지금 보는 채널 | `applyCurrentRow` + 앵커 | **초 단위로 참** |
   * | 나머지 | 이 응답 | **부팅 시점** |
   *
   * 2026-09-04 무대 신고가 정확히 그것이었다 — 1번 채널을 Ep1@152 에서 이어보다 Ep2 로
   * 넘어간 뒤 2번 채널로 갔더니, 편성표의 1번 행이 **다시 Ep1 을 「지금」으로** 그렸다.
   * 되돌아오면 또 Ep2 다. **두 그림 다 「그럴듯해서」** 어느 쪽이 틀렸는지 화면으로는
   * 안 갈린다.
   *
   * 그리고 그것은 절대 규칙 68 이 지키려는 바로 그 문장을 깬다 — **「EPG 의 첫 카드는 그
   * 채널을 켰을 때 실제로 나오는 것이다」.** 낡은 행의 「지금」 카드를 누르면 서버의
   * 이어보기로 들어가므로 **누른 회차와 나오는 회차가 다르다.**
   *
   * **채널 전환에 걸지 않는다** — 떠나는 회차의 진행 보고(`teardown`)와 이 요청이
   * 같은 순간에 나가 **어느 것이 먼저 서버에 닿을지 정해져 있지 않다.** 여는 순간은
   * 그보다 한참 뒤라 그 경합이 없다.
   */
  /**
   * v28. ONAIR 편성 — **앱이 ONAIR 서버에서 직접 받는다** (절대 규칙 75 · `docs/57` D7).
   *
   * 개인화 편성과 **따로** 든다. 합치면 한쪽이 실패했을 때 다른 쪽까지 자리표시가 되고,
   * 그 둘은 **서로 다른 서버**라 함께 죽을 이유가 없다.
   */
  const [onairLineups, setOnairLineups] = useState<ReadonlyMap<string, OnairChannelLineup>>(
    new Map(),
  );

  const refreshLineup = useCallback(
    (signal?: AbortSignal) =>
      fetchLineup({
        deviceId: tv2m.init?.deviceId,
        token: auth.token,
        ...(signal ? { signal } : {}),
        // **실패(`null`)면 들고 있던 것을 지킨다** — 중단(`abort`)도 그 갈래다.
      }).then((items) => {
        if (items !== null) setLineups(items);
      }),
    [tv2m.init, auth.token],
  );

  useEffect(() => {
    const controller = new AbortController();
    void refreshLineup(controller.signal).then(() => {
      if (!controller.signal.aborted) setLineupReady(true);
    });
    // **상한이 있다** — 편성표가 늦어도 홈은 선다. 그때는 기기 저장값의 채널이고, 편성표가 오면 바뀐다.
    const cap = window.setTimeout(() => {
      setLineupReady(true);
    }, LINEUP_WAIT_CAP_MS);
    return () => {
      controller.abort();
      window.clearTimeout(cap);
    };
  }, [refreshLineup]);

  /**
   * v28. ONAIR 편성도 **여는 순간 다시 받는다** (규칙 73 의 ★ 승계 · `refreshLineup` 과
   * 같은 자리). 안 하면 그 행이 **부팅 시점의 스냅숏으로 굳고**, 시각축이라 그 낡음이
   * 개인화 행보다 **더 크게** 보인다 — 상자가 통째로 몇 분 어긋난다.
   */
  const refreshOnair = useCallback(
    async (signal?: AbortSignal) => {
      if (channels === null) return;
      const found = await fetchOnairLineups(channels, signal);
      // **빈 맵으로 덮어쓰지 않는다** — 전부 실패했으면 들고 있던 것을 지킨다.
      if (found.size > 0) setOnairLineups(found);
    },
    [channels],
  );

  useEffect(() => {
    const controller = new AbortController();
    void refreshOnair(controller.signal);
    return () => {
      controller.abort();
    };
  }, [refreshOnair]);

  /*
   * ★ **전환 뒤 게이트가 사라졌다** (v19, 2026-08-20).
   *
   * v12 는 여기서 iPhone 만 게이트를 한 번 더 세웠다. **WebKit 은 소리 있는 재생 허가를
   * 엘리먼트 단위로 기억하는데** 전환이 `key` 재마운트로 `<video>` 를 새로 만들었고,
   * 전환의 `start()` 는 `useEffect` 안이라 **제스처 밖**이라 그대로 실패했다.
   *
   * **이제 엘리먼트를 새로 만들지 않는다** — 앱이 한 쌍을 들고 있다가 어댑터에 넘긴다
   * (`playback-buffers.ts`). 허가가 그 엘리먼트에 남아 있으므로 제스처 밖에서도 시작되고,
   * 그것이 자연 전환·스킵·선택이 아이폰에서 늘 멀쩡했던 이유와 같다.
   *
   * **되돌리려면 `setAutoStart(true)` 를 `setAutoStart(resolvePlayerKind() === 'hls')` 로**
   * 바꾸면 v12 의 동작으로 돌아간다.
   */

  /**
   * 재생 중 채널 전환 (`docs/23` §7.4). **순서가 계약이다.**
   *
   * ```
   * 1. 옛 리시버 파괴        ← setSwitching(next) 가 WatchScreen 을 내린다
   * 2. 새 채널의 플레이리스트  ← 새 WatchScreen 이 마운트되며 부른다
   * 3. 리시버 재생성·재생 시작 ← autoStart. **iOS 는 여기 앞에 게이트가 한 번 선다**
   * 4. localStorage 저장
   * 5. 상태 갱신             ← 새 마운트라 오버레이·핸드오프가 전부 초기값이다
   * ```
   *
   * **같은 채널이면 아무 일도 하지 않는다.** 여기서 걸러야 「같은 걸 눌렀는데 영상이
   * 처음부터 다시 시작한다」가 생기지 않는다.
   */
  const switchTo = useCallback(
    (next: string, from: string, at: string | null = null, fromEpg = false) => {
    // **회차를 함께 골랐어도 같은 채널이면 여기 오지 않는다** — 가르는 것은 화면이고
    // 판정은 `resolveEpgPick` 이 한다 (v25, 절대 규칙 69). 가드를 풀면 같은 채널에서
    // 리시버가 통째로 다시 서서 **영상이 끊긴다.**
    if (next === from) return;
    /*
     * v28. **화면이 비기 전에 마지막 프레임을 잡는다** (`freeze-frame.ts`).
     *
     * 여기가 그 자리인 이유는 **다음 줄부터 옛 화면이 내려가기 때문**이다 —
     * `setSwitching` 이 `WatchScreen` 을 언마운트하면 `<video>` 가 문서에서 떨어지고,
     * 떨어진 엘리먼트는 `drawImage` 로 그려도 **빈 사각형**이다.
     *
     * 실패해도 전환은 그대로 간다 — 첫 진입과 아직 프레임이 없는 순간이 그 갈래이고,
     * 그때는 지금까지와 똑같이 검은 화면이다.
     */
    captureFreezeFrame(playbackBuffers());
    writeSavedChannel(next);
    setAutoStart(true);
    setStartAt(at);
    // v29. **매번 쓴다** — 남기면 다음 ▲▼ 전환이 편성표를 달고 시작한다.
    setKeepEpg(fromEpg);
    setSwitching(next);
    },
    [],
  );

  // 1번(파괴)이 끝난 다음 렌더에서 새 채널을 세운다. 같은 렌더에서 바꾸면 React 가
  // 한 커밋 안에서 처리해 **비어 있는 순간이 없어지고**, 그러면 규칙 27 이 지켜지는
  // 근거가 다시 「React 가 정리를 먼저 돌린다」는 내부 동작이 된다.
  useEffect(() => {
    if (switching === null) return;
    setPicked(switching);
    setSwitching(null);
  }, [switching]);

  const select = useCallback((channelId: string, at: string | null = null) => {
    // **고른 값만 저장한다.** `?channel=` 과 `VITE_CHANNEL_ID` 는 저장하지 않는다 —
    // 하네스와 데모 운영자가 잠시 고정해 보는 통로이지 사람의 선택이 아니다.
    writeSavedChannel(channelId);
    setStartAt(at);
    setPicked(channelId);
    setListing(false);
  }, []);

  // 전환 중이다. **여기서 `WatchScreen` 이 내려가 옛 리시버가 파괴된다** (규칙 27 의 1번).
  // 인디케이터를 그리지 않는다 — 전환 중 인디케이터는 그것 자체가 끊김으로 보인다
  // (`docs/06` 「상태 표시」).
  if (switching !== null) {
    /*
     * v28. **비어 있던 그 한 렌더가 마지막 프레임 잡기의 첫 조각이다** — 잡아 둔 것이
     * 없으면 `FreezeFrame` 이 아무것도 그리지 않아 지금까지와 같다.
     *
     * ★ v29. **편성표에서 고른 전환이면 그 시트를 여기서도 그린다** (2026-09-06).
     * 안 그리면 이 한 커밋 동안 시트가 사라지고 **잡아 둔 옛 채널의 프레임이 한 프레임
     * 드러난다** — 새 화면이 곧바로 다시 그려도 그 깜빡임이 남는다. 화면 셋이 같은
     * 시트를 그리는 자리가 그래서 넷이 됐다.
     *
     * `now` 가 `null` 이다 — 이 순간 재생 중인 것이 **정말로 없다**(규칙 46).
     */
    return (
      <div className="stage">
        <FreezeFrame />
        {keepEpg && (
          <ChannelSheet
            rows={toEpgRows(channels ?? [], lineups, {
              adsEnabled,
              lastItems: auth.token === null ? readLastItems() : {},
              onair: onairLineups,
            })}
            currentChannelId={null}
            currentProductId={null}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
            signedIn={auth.token !== null}
            onPick={(nextChannelId) => {
              // 전환 중에 또 골랐다. **같은 채널이면 `switchTo` 의 가드가 막는다.**
              switchTo(nextChannelId, switching, null, true);
            }}
            onClose={() => {
              setKeepEpg(false);
            }}
            now={null}
          />
        )}
      </div>
    );
  }

  // 목록을 받기 전에는 아무것도 그리지 않는다. 여기서 시청 화면을 한 번 그리면
  // 채널이 확정된 뒤 리시버가 두 번 서고, 그 상태의 전환 수치는 뜻이 없다.
  const loadingCard = (
    <div className="app">
      <AppHeader />
      <div className="card">
        <p className="card__text">{t('common.loading')}</p>
      </div>
    </div>
  );
  if (channels === null) return loadingCard;

  const choice = resolveChannel({
    preferred: picked ?? config.channelId,
    saved: readSavedChannel(),
    channels,
  });
  const current = choice.kind === 'play' ? choice.channelId : null;

  /*
   * v26 — **홈쇼핑 채널의 시작점** (절대 규칙 70).
   *
   * ★ **자물쇠 ②가 여기서 걸린다** — `entryStartFor` 는 `kind` 가 `homeshopping` 이 아니면
   * 언제나 `null` 이다. 종류는 **채널 목록이 이미 갖고 있어** 재생 전에 판정된다
   * (`view.channelKind` 는 `playlistLoaded` 에서야 오므로 그것으로는 이 판정을 할 수 없다 —
   * 절대 규칙 49 가 게이트에 대해 적어 둔 그 자리와 같은 사정이다).
   *
   * 렌더마다 저장소를 읽지만 그 값은 **마운트에서 한 번 쓰이고 버려진다**
   * (`useReceiver` 의 「한 번 쓰고 버린다」).
   */
  // 2026-09-10. **로그인하면 계정 행이 진실이다** — 기기의 기억(`bloom.lastItems`)은 비로그인의 것(규칙 70 개정).
  const lastItems = auth.token === null ? readLastItems() : {};
  const resumeAt =
    current === null
      ? null
      : entryStartFor(channels.find((c) => c.channelId === current)?.kind, lastItems, current);

  /**
   * ★ v32 — **무엇을 그릴지** (`docs/63` §3.1 · 절대 규칙 81). 시작 게이트가 서던 조건이 곧
   * 홈의 조건이다 — `debug`(하네스) 와 `unlocked`(이 문서에 이미 허가) 는 지금 그대로
   * `'play'` 이고, `'play'` 갈래는 **한 글자도 안 바뀐다**(`WatchScreen` 이 `?debug=1` 과
   * 거부 되돌림에서 옛 게이트를 그린다).
   */
  const surface = entrySurface({
    debug: config.debug,
    unlocked: buffersUnlocked(),
    installedAutoStart,
    homeRequested: home,
    homeForInstalled: HOME_FOR_INSTALLED,
  });

  // `/channels` 로 들어온 전체화면 편성표(`forceList`)가 먼저다. 홈의 편성표는 홈 위의 시트다.
  if (surface === 'home' && !listing) {
    // v32. 편성표가 올 때까지 — 대상 채널이 계정의 최근 시청으로 정해지기 때문(위 `lineupReady`).
    if (!lineupReady) return loadingCard;
    return (
      <HomeScreen
        rows={toEpgRows(channels, lineups, { adsEnabled, lastItems, onair: onairLineups })}
        choice={choice}
        favorites={favorites}
        runtime={runtime}
        tv2m={tv2m}
        auth={auth}
        lastContext={lastContext}
        onOpenLast={onOpenLast}
        /*
         * 대상 채널의 OK — **저장하지 않는다**(`docs/23` §7.3 · `?channel=` 이 섞여 있다).
         * `picked` 만 세워 `resolveChannel` 이 그 채널을 내게 하고, `startAt` 은 「지금 판매 중」
         * 카드의 상품이다(v11 의 `selectItem` 경로).
         */
        onWatchTarget={(channelId, at) => {
          setAutoStart(true);
          setHome(false);
          setStartAt(at);
          setPicked(channelId);
        }}
        /* 다른 채널 — 사람이 골랐으므로 `select` 가 저장한다 (A14) */
        onPickChannel={(channelId) => {
          setAutoStart(true);
          setHome(false);
          select(channelId, null);
        }}
        onToggleFavorite={toggleFavorite}
        /* 시트를 여는 순간 다시 받는다 (절대 규칙 73 ②) — 시청 화면의 `onEpgOpen` 과 같은 줄 */
        onEpgOpen={() => {
          void refreshLineup();
          void refreshOnair();
        }}
        /*
         * 홈 위의 편성표 시트에서 고른 것 — 옛 게이트의 「방송편성 보기」와 같은 뜻이라 `select`
         * 로 저장하고(사람이 골랐다) 게이트 없이 바로 재생이다(`unlockBuffers` 는 홈이 제스처
         * 안에서 이미 불렀다).
         */
        onPickFromGuide={(channelId, at) => {
          setAutoStart(true);
          setHome(false);
          select(channelId, at);
        }}
        /*
         * 시청에서 돌아온 홈만 편성표를 다시 받는다 — 첫 진입은 방금 받았다. 지연을 두는
         * 이유는 떠난 채널의 `teardown` 보고와의 경합이다(규칙 73 ② · `docs/63` §3.3 ⑦).
         */
        onRefresh={
          home
            ? () => {
                void refreshLineup();
                void refreshOnair();
              }
            : null
        }
      />
    );
  }

  if (listing || choice.kind !== 'play') {
    return (
      <div className="app app--wide">
        <AppHeader />
        <EpgScreen
          rows={toEpgRows(channels, lineups, { adsEnabled, lastItems, onair: onairLineups })}
          /*
           * ★ **여기서는 아무것도 재생되고 있지 않다** (2026-09-06). `current` 는
           * 「들어가면 틀 채널」이지 「지금 트는 채널」이 아니다 — 이 분기가 그려지는
           * 동안 `WatchScreen` 은 내려가 있다. 아래 `now={null}` 과 **같은 사실**이고,
           * 둘이 어긋나면 화면이 「아무것도 안 흐르는데 보는 중」이라고 말한다.
           */
          currentChannelId={null}
          currentProductId={null}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          signedIn={auth.token !== null}
          onPick={(channelId, productId, isEntryCard) => {
            // 전체화면이라 「지금 보는 것」이 없다 — 언제나 그 채널로 들어간다.
            // **첫 장이면 회차를 가리키지 않는다** (절대 규칙 68).
            select(channelId, isEntryCard ? null : productId);
          }}
          /* 게이트에서는 아무것도 재생되고 있지 않다 — **눈금만 흐른다** */
          now={null}
          onBack={
            listing && current !== null
              ? () => {
                  setListing(false);
                }
              : null
          }
        />
      </div>
    );
  }

  /*
   * ★ **v28 — 재생 화면이 둘이다** (`docs/57` §8 · D9).
   *
   * 가르는 것은 `delivery` 하나이고, **`WatchScreen` 에 분기를 넣지 않았다** — 저 화면은
   * 1,700줄이고 리시버가 있다는 전제가 깊어(`view.started`·`getPlayhead`·광고·상품·
   * 핸드오프·진행 보고) 분기를 넣으면 그 전제들이 「있을 수도 없을 수도」가 되고,
   * **없는 쪽의 조합이 영원히 안 밟히는 코드**가 된다.
   *
   * **전환 절차는 같다** — `key` 로 못박고(규칙 27), `switching` 이 한 렌더를 비운다.
   */
  const playing = channels.find((c) => c.channelId === choice.channelId);
  if (playing?.delivery === 'onair') {
    return (
      <OnairScreen
        key={choice.channelId}
        channel={playing}
        autoStart={autoStart}
        runtime={runtime}
        tv2m={tv2m}
        auth={auth}
        epgRows={toEpgRows(channels, lineups, { adsEnabled, lastItems, onair: onairLineups })}
        onEpgOpen={() => {
          void refreshLineup();
          void refreshOnair();
        }}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
        epgOpen={keepEpg}
        onSwitchChannel={(next, fromEpg = false) => {
          switchTo(next, choice.channelId, null, fromEpg);
        }}
        onHome={() => {
          setHome(true);
        }}
      />
    );
  }

  return (
    <WatchScreen
      /*
       * **`key` 로 채널을 못박는다.** 채널이 바뀌면 이 화면이 통째로 새로 서고, 그
       * 과정에서 `useReceiver` 의 정리가 먼저 돌아 옛 리시버가 파괴된다 (절대 규칙 27).
       * 상태를 이어받으면 앞 채널의 오버레이·핸드오프가 새 채널 위에 남는다.
       */
      key={choice.channelId}
      channelId={choice.channelId}
      autoStart={autoStart}
      runtime={runtime}
      tv2m={tv2m}
      auth={auth}
      lastContext={lastContext}
      onOpenLast={onOpenLast}
      /*
       * **목록을 통째로 넘긴다.** 재생 중에는 화면을 옮기지 않고 시트로 연다 —
       * 옮기면 이 화면이 내려가 리시버가 파괴되고, 그냥 둘러보려던 시청자의 재생이
       * 끊긴다 (절대 규칙 14).
       */
      epgRows={toEpgRows(channels, lineups, { adsEnabled, lastItems, onair: onairLineups })}
      /*
       * v27/TG15. **시트가 열리는 순간 편성표를 다시 받는다** (위 ★★).
       * 재생 중인 행은 앵커가 이미 참이고, 갱신되는 것은 **나머지 행**이다.
       */
      onEpgOpen={() => {
        void refreshLineup();
        // v28. **셋째 출처도 함께 다시 받는다** — 한쪽만 갱신하면 두 행의 나이가 갈린다.
        void refreshOnair();
      }}
      favorites={favorites}
      onToggleFavorite={toggleFavorite}
      startAt={startAt}
      /*
       * v26. **`startAt` 과 다른 prop 이다** (절대 규칙 70) — 저쪽은 시청자가 고른 것이라
       * `selectItem()` 으로 가고 `select` 이벤트가 나가야 하며, 이쪽은 기기가 기억한
       * 것이라 `start({ from })` 으로 가고 **나가면 안 된다.** 합치면 그 갈림이 사라진다.
       */
      resumeAt={resumeAt}
      epgOpen={keepEpg}
      onSwitchChannel={(next, at, fromEpg) => {
        switchTo(next, choice.channelId, at ?? null, fromEpg);
      }}
      /*
       * v32. 「홈」 — 이 화면을 내리고(리시버 파괴 · 규칙 27) 홈을 그린다. `entrySurface` 의
       * `homeRequested` 가 전부를 이기므로 `?debug=1` 에서도 홈이다.
       */
      onHome={() => {
        setHome(true);
      }}
    />
  );
}
